// Detalle de UNA sesión para la vista del kine (DetalleSesionKine).
// Arma el shape `Sesion` del componente a partir de la fila de `sesiones`
// + su pose_analysis, y la compara con la sesión completada anterior del
// mismo paciente y ejercicio.
//
// Reps: si pose_analysis trae el detalle por rep (series[].reps), se usa;
// si no (sesiones grabadas por la app, que solo guardan agregados por
// serie), se aproxima una rep por cada rep detectada con los promedios de
// su serie.

import { useEffect, useState } from "react";
import { supabase } from "../client";
import { getExerciseSchema } from "@/lib/pose/exerciseSchemas";
import type {
  Sesion,
  SesionRep,
} from "@/app/components/sessions/DetalleSesionKine";

interface SerieJson {
  reps_detectados?: number;
  calidad_promedio?: number;
  duracion_promedio_ms?: number;
  prominencia_promedio_grados?: number;
  reps?: Array<{ n: number; angulo_max: number; calidad: number; valida: boolean }>;
}

interface PoseJson {
  ejecucion?: { series?: SerieJson[] };
  calidad_global?: { violaciones_frecuentes?: Array<{ tipo: string }> };
}

interface Row {
  id: string;
  started_at: string;
  duration_sec: number | null;
  total_reps: number;
  score_promedio: number | null;
  ejercicio_id: string;
  paciente_id: string;
  pose_analysis: PoseJson | null;
  ejercicios: { nombre: string } | null;
  pacientes: { nombre: string; apellido: string } | null;
  plan_ejercicios: { reps_objetivo: number; series_objetivo: number } | null;
}

const SELECT =
  "id, started_at, duration_sec, total_reps, score_promedio, ejercicio_id, paciente_id, pose_analysis, ejercicios(nombre), pacientes(nombre, apellido), plan_ejercicios(reps_objetivo, series_objetivo)";

function repsDe(pose: PoseJson | null): SesionRep[] {
  const series = pose?.ejecucion?.series ?? [];
  const violacion = pose?.calidad_global?.violaciones_frecuentes?.[0]?.tipo;
  const reps: SesionRep[] = [];
  for (const s of series) {
    const detalle =
      s.reps ??
      Array.from({ length: s.reps_detectados ?? 0 }, (_, i) => ({
        n: i + 1,
        angulo_max: s.prominencia_promedio_grados ?? 0,
        calidad: s.calidad_promedio ?? 0,
        valida: true,
      }));
    for (const r of detalle) {
      reps.push({
        n: reps.length + 1,
        angleMax: r.angulo_max,
        durationMs: s.duracion_promedio_ms ?? 0,
        quality: Math.round(r.calidad * 100),
        violations: !r.valida && violacion ? [violacion] : [],
      });
    }
  }
  return reps;
}

function promedio(nums: number[]): number {
  return nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : 0;
}

export interface DetalleSesionKineData {
  sesion: Sesion;
  /** violationId → label legible, desde las rules del schema. */
  violationLabels: Record<string, string>;
}

export function useDetalleSesionKine(sessionId: string | undefined): {
  data: DetalleSesionKineData | null;
  loading: boolean;
  error: string | null;
} {
  const [state, setState] = useState<{
    data: DetalleSesionKineData | null;
    loading: boolean;
    error: string | null;
  }>({ data: null, loading: true, error: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ data: null, loading: false, error: "Sesión no encontrada" });
      return;
    }
    let cancelled = false;
    void (async () => {
      const { data: row, error } = await supabase
        .from("sesiones")
        .select(SELECT)
        .eq("id", sessionId)
        .maybeSingle();
      if (cancelled) return;
      if (error || !row) {
        setState({ data: null, loading: false, error: error?.message ?? "Sesión no encontrada" });
        return;
      }
      const s = row as unknown as Row;

      const { data: prevRows } = await supabase
        .from("sesiones")
        .select("score_promedio, pose_analysis")
        .eq("paciente_id", s.paciente_id)
        .eq("ejercicio_id", s.ejercicio_id)
        .eq("estado", "completada")
        .lt("started_at", s.started_at)
        .order("started_at", { ascending: false })
        .limit(1);
      if (cancelled) return;

      const reps = repsDe(s.pose_analysis);
      const series = s.pose_analysis?.ejecucion?.series ?? [];
      const calidadPct = Math.round((s.score_promedio ?? 0) * 100);
      const contarViolaciones = (rs: SesionRep[]) =>
        rs.reduce((a, r) => a + r.violations.length, 0);

      const prev = prevRows?.[0] as
        | { score_promedio: number | null; pose_analysis: PoseJson | null }
        | undefined;
      const prevReps = prev ? repsDe(prev.pose_analysis) : [];

      const schema = getExerciseSchema(s.ejercicio_id);
      const violationLabels: Record<string, string> = {};
      for (const rule of schema?.rules ?? []) {
        if (rule.violationId) {
          violationLabels[rule.violationId] = rule.summaryLabel ?? rule.feedback ?? rule.violationId;
        }
      }

      setState({
        loading: false,
        error: null,
        data: {
          violationLabels,
          sesion: {
            id: s.id,
            fecha: s.started_at,
            paciente: {
              id: s.paciente_id,
              nombre: s.pacientes ? `${s.pacientes.nombre} ${s.pacientes.apellido}` : "Paciente",
            },
            ejercicio: { id: s.ejercicio_id, nombre: s.ejercicios?.nombre ?? s.ejercicio_id },
            duracionSeg: s.duration_sec ?? 0,
            repsTotal: s.total_reps,
            repsObjetivo:
              (s.plan_ejercicios?.reps_objetivo ?? 0) * (s.plan_ejercicios?.series_objetivo ?? 0) ||
              s.total_reps,
            seriesCompletas: series.length,
            seriesObjetivo: s.plan_ejercicios?.series_objetivo ?? series.length,
            calidadPct,
            reps,
            comparacionAnterior: prev
              ? {
                  calidadPctDelta: calidadPct - Math.round((prev.score_promedio ?? 0) * 100),
                  angleMaxDelta: Math.round(
                    promedio(reps.map((r) => r.angleMax)) -
                      promedio(prevReps.map((r) => r.angleMax)),
                  ),
                  violacionesDelta: contarViolaciones(reps) - contarViolaciones(prevReps),
                }
              : undefined,
          },
        },
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return state;
}
